/*
  Wholey Loop Bundle Builder
  Custom build-your-own-bundle UI on top of the Loop Subscriptions Storefront API.

  Flow (see https://loop-storefront.readme.io/reference/bundle-apis-overview):
    1. GET  {apiBase}/bundle/{bundleId}            -> box sizes, discounts, selling plans
    2. POST {apiBase}/bundle/{bundleId}/transaction -> { txnId }
    3. POST /cart/add.js with property `_bundleId: txnId` on every line
       (loop_bundle.js groups cart lines by this property to render them as one bundle)
*/
(function () {
  'use strict';

  class LoopBundleAPI {
    constructor(apiBase, myshopifyDomain, configuredBundleId) {
      this.apiBase = apiBase.replace(/\/$/, '');
      this.shop = myshopifyDomain;
      this.configuredBundleId = configuredBundleId || null;
      this.bundleIdPromise = null;
      this.detailsPromise = null;
    }

    // https://developer.loopwork.co/reference/list-bundles
    // The id returned by the list is the one the details and transaction
    // endpoints expect. With no configured id, the first active bundle is used.
    resolveBundleId() {
      if (!this.bundleIdPromise) {
        this.bundleIdPromise = fetch(`${this.apiBase}/bundle?myshopifyDomain=${encodeURIComponent(this.shop)}`)
          .then((response) => {
            if (!response.ok) throw new Error(`Loop list bundles failed: ${response.status}`);
            return response.json();
          })
          .then((json) => {
            const bundles = (json.data || []).filter((bundle) => bundle.status === 'ACTIVE');
            const match = this.configuredBundleId
              ? bundles.find(
                  (bundle) =>
                    String(bundle.id) === this.configuredBundleId || bundle.name === this.configuredBundleId
                )
              : null;
            const bundle = match || bundles[0];
            if (!bundle) throw new Error('No active Loop bundle found for this shop');
            return bundle.id;
          })
          .catch((error) => {
            this.bundleIdPromise = null;
            throw error;
          });
      }
      return this.bundleIdPromise;
    }

    // Bundle details are public storefront data; cache the promise so the
    // request fires once even when called from init and from add-to-cart.
    getDetails() {
      if (!this.detailsPromise) {
        this.detailsPromise = this.resolveBundleId()
          .then((bundleId) => fetch(`${this.apiBase}/bundle/${encodeURIComponent(bundleId)}`))
          .then((response) => {
            if (!response.ok) throw new Error(`Loop bundle details failed: ${response.status}`);
            return response.json();
          })
          .then((json) => json.data || json)
          .catch((error) => {
            console.warn('[loop-bundle-builder]', error);
            this.detailsPromise = null;
            return null;
          });
      }
      return this.detailsPromise;
    }

    async createTransaction({ boxSizeId, discountId, sellingPlanShopifyId, productVariants }) {
      const bundleId = await this.resolveBundleId();
      const response = await fetch(`${this.apiBase}/bundle/${encodeURIComponent(bundleId)}/transaction`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          bundleId,
          boxSizeId: boxSizeId ?? null,
          discountId: discountId ?? null,
          sellingPlanShopifyId: sellingPlanShopifyId ?? null,
          quantity: 1,
          productVariants,
        }),
      });
      if (!response.ok) throw new Error(`Loop transaction failed: ${response.status}`);
      const json = await response.json();
      const txnId = json.data?.txnId || json.txnId;
      if (!txnId) throw new Error('Loop transaction response contained no txnId');
      return txnId;
    }
  }

  class LoopBundleBuilder extends HTMLElement {
    connectedCallback() {
      this.minQuantity = parseInt(this.dataset.minQuantity, 10) || 6;
      this.tiers = JSON.parse(this.dataset.tiers || '[]').sort((a, b) => a.quantity - b.quantity);
      this.maxTierQuantity = this.tiers.length ? this.tiers[this.tiers.length - 1].quantity : this.minQuantity;
      this.sellingPlanId = this.dataset.sellingPlanId || null;
      this.api = window.Shopify?.shop
        ? new LoopBundleAPI(this.dataset.apiBase, window.Shopify.shop, this.dataset.bundleId)
        : null;

      this.selection = new Map();
      // a product can appear in several category collections, so one variant
      // may have multiple card instances that must stay in sync
      this.cards = new Map();
      this.querySelectorAll('[data-bundle-card]').forEach((card) => {
        const cards = this.cards.get(card.dataset.variantId) || [];
        cards.push(card);
        this.cards.set(card.dataset.variantId, cards);
      });
      this.restoreSelection();

      this.checkoutButton = this.querySelector('[data-bundle-checkout]');

      // "x Produkte"-Übersicht: Hover auf Geräten mit Maus, Tap auf Touch
      this.summary = this.querySelector('[data-bundle-summary]');
      this.summaryToggle = this.querySelector('[data-summary-toggle]');
      if (this.summary) {
        this.hoverMedia = window.matchMedia('(hover: hover) and (pointer: fine)');
        this.summary.addEventListener('mouseenter', () => {
          if (this.hoverMedia.matches) this.toggleSummary(true);
        });
        this.summary.addEventListener('mouseleave', () => {
          if (this.hoverMedia.matches) this.toggleSummary(false);
        });
        document.addEventListener('click', (event) => {
          if (this.summaryOpen && !this.summary.contains(event.target)) this.toggleSummary(false);
        });
        document.addEventListener('keydown', (event) => {
          if (event.key === 'Escape') this.toggleSummary(false);
        });
      }

      this.addEventListener('click', this.onClick.bind(this));

      this.maybeOpenCartDrawer();
      this.initAnchorSpy();
      this.verifyActiveBundle();

      if (this.api) {
        // prefer the tiers configured in Loop so UI and checkout always match
        this.api.getDetails().then((details) => {
          const tiers = (details?.discounts || [])
            .filter((discount) => discount.conditionType === 'QUANTITY' && discount.type === 'PERCENTAGE')
            .map((discount) => ({ quantity: discount.minCartQuantity, discount: discount.value }))
            .sort((a, b) => a.quantity - b.quantity);
          if (tiers.length) {
            this.tiers = tiers;
            this.maxTierQuantity = tiers[tiers.length - 1].quantity;
            this.render();
          }
        });
      }
      this.render();
    }

    // after the post-add reload, open the cart drawer with the fresh cart
    maybeOpenCartDrawer() {
      try {
        if (!sessionStorage.getItem('loop-bb:open-cart-drawer')) return;
        sessionStorage.removeItem('loop-bb:open-cart-drawer');
      } catch {
        return;
      }
      customElements.whenDefined('cart-drawer').then(() => {
        setTimeout(() => document.querySelector('cart-drawer')?.show(), 400);
      });
    }

    // mark the anchor of the category currently in view while scrolling
    initAnchorSpy() {
      const anchors = Array.from(this.querySelectorAll('.loop-bb__anchor'));
      const targets = anchors
        .map((anchor) => ({ anchor, category: this.querySelector(anchor.getAttribute('href')) }))
        .filter((target) => target.category);
      if (!targets.length) return;

      const bar = this.querySelector('.loop-bb__anchors-bar');
      let ticking = false;

      const update = () => {
        ticking = false;
        // reference line just below the sticky anchor bar
        const line = (bar ? bar.getBoundingClientRect().bottom : 0) + 8;
        let active = null;
        targets.forEach((target) => {
          if (target.category.getBoundingClientRect().top <= line) active = target;
        });
        if (active === this.activeAnchorTarget) return;
        this.activeAnchorTarget = active;
        targets.forEach((target) => target.anchor.classList.toggle('is-active', target === active));
        // keep the active pill visible in the horizontally scrollable nav
        if (active) {
          const nav = active.anchor.closest('.loop-bb__anchors');
          if (nav && nav.scrollWidth > nav.clientWidth) {
            nav.scrollTo({
              left: active.anchor.offsetLeft - (nav.clientWidth - active.anchor.offsetWidth) / 2,
              behavior: 'smooth'
            });
          }
        }
      };

      this.onAnchorSpyScroll = () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(update);
      };
      window.addEventListener('scroll', this.onAnchorSpyScroll, { passive: true });
      update();
    }

    disconnectedCallback() {
      if (this.onAnchorSpyScroll) window.removeEventListener('scroll', this.onAnchorSpyScroll);
    }

    onClick(event) {
      const summaryToggle = event.target.closest('[data-summary-toggle]');
      if (summaryToggle) {
        // auf Hover-Geräten steuert mouseenter/-leave; Klick nur für Touch
        // und Tastatur (event.detail === 0 = per Enter/Space ausgelöst)
        if (!this.hoverMedia?.matches || event.detail === 0) this.toggleSummary(!this.summaryOpen);
        return;
      }

      const step = event.target.closest('[data-quantity-change]');
      if (step) {
        const card = step.closest('[data-bundle-card]');
        this.changeQuantity(card.dataset.variantId, parseInt(step.dataset.quantityChange, 10));
        return;
      }

      const detail = event.target.closest('[data-open-detail]');
      if (detail) {
        this.openDetail(detail.dataset.openDetail);
        return;
      }

      const tile = event.target.closest('[data-pack-tile]');
      if (tile) {
        this.selectGoal(parseInt(tile.dataset.packTile, 10));
        return;
      }

      if (event.target.closest('[data-bundle-checkout]')) {
        this.addToCart();
      }
    }

    changeQuantity(variantId, delta) {
      if (!this.cards.has(variantId)) return;
      const current = this.selection.get(variantId) || 0;
      const next = Math.max(0, current + delta);
      if (next === 0) {
        this.selection.delete(variantId);
      } else {
        this.selection.set(variantId, next);
      }
      this.render();
    }

    selectGoal(quantity) {
      this.goal = quantity;
      this.querySelectorAll('[data-pack-tile]').forEach((tile) => {
        tile.classList.toggle('is-selected', parseInt(tile.dataset.packTile, 10) === quantity);
        tile.setAttribute('aria-pressed', parseInt(tile.dataset.packTile, 10) === quantity);
      });
      this.render();
    }

    get totalQuantity() {
      let total = 0;
      this.selection.forEach((quantity) => (total += quantity));
      return total;
    }

    get subtotalCents() {
      let subtotal = 0;
      this.selection.forEach((quantity, variantId) => {
        const card = this.cards.get(variantId)[0];
        subtotal += quantity * parseInt(card.dataset.price, 10);
      });
      return subtotal;
    }

    // Highest tier whose quantity threshold is reached; above the last tier the
    // discount stays capped at that tier (12+ products keep 10%).
    activeTier(quantity = this.totalQuantity) {
      let active = null;
      for (const tier of this.tiers) {
        if (quantity >= tier.quantity) active = tier;
      }
      return active;
    }

    nextTier(quantity = this.totalQuantity) {
      return this.tiers.find((tier) => quantity < tier.quantity) || null;
    }

    formatMoney(cents) {
      if (window.theme?.utils?.formatMoney) {
        return theme.utils.formatMoney(cents, theme.settings.moneyFormat);
      }
      return (cents / 100).toFixed(2).replace('.', ',') + ' €';
    }

    // Loop discounts each unit price and rounds it UP to the next cent
    // (4,99 € − 10% → 4,50 €), so the builder must sum per-unit prices the
    // same way to match the cart exactly.
    discountedUnitPrice(unitCents, discountPercent) {
      return Math.ceil(unitCents * (1 - discountPercent / 100));
    }

    discountedTotalCents(discountPercent) {
      if (!discountPercent) return this.subtotalCents;
      let total = 0;
      this.selection.forEach((quantity, variantId) => {
        const unit = parseInt(this.cards.get(variantId)[0].dataset.price, 10);
        total += quantity * this.discountedUnitPrice(unit, discountPercent);
      });
      return total;
    }

    render() {
      const quantity = this.totalQuantity;
      const subtotal = this.subtotalCents;
      const tier = this.activeTier();
      const discountPercent = tier ? tier.discount : 0;
      const total = this.discountedTotalCents(discountPercent);
      const savings = subtotal - total;

      // progress bar: 100% = last tier threshold
      const progress = Math.min(100, (quantity / this.maxTierQuantity) * 100);
      this.querySelectorAll('[data-progress-fill]').forEach((el) => {
        el.style.width = `${progress}%`;
      });
      // gelb unterhalb der Mindestmenge, grün ab Erreichen (psychologischer Anreiz)
      this.querySelectorAll('.loop-bb__progress').forEach((bar) => {
        bar.classList.toggle('is-goal', quantity >= this.minQuantity);
      });
      this.querySelectorAll('[data-progress-marker]').forEach((marker) => {
        const reached = quantity >= parseInt(marker.dataset.progressMarker, 10);
        marker.classList.toggle('is-reached', reached);
      });

      // Vorteils-Chips (Gratis Versand / Geschenk) am rabattierten Bundle-Wert messen
      this.querySelectorAll('[data-bundle-perk]').forEach((perk) => {
        const threshold = parseInt(perk.dataset.threshold, 10);
        const reached = total >= threshold;
        perk.classList.toggle('is-reached', reached);
        perk.textContent = reached
          ? perk.dataset.textReached
          : perk.dataset.textMissing.replace('[amount]', this.formatMoney(threshold - total));
      });

      this.setText('[data-bundle-count]', quantity);
      if (this.summaryToggle) {
        this.summaryToggle.disabled = quantity === 0;
        if (quantity === 0) this.toggleSummary(false);
      }
      this.renderSummary();
      this.setText('[data-bundle-subtotal]', this.formatMoney(subtotal));
      this.setText('[data-bundle-total]', this.formatMoney(total));

      this.querySelectorAll('[data-bundle-discount]').forEach((badge) => {
        badge.hidden = discountPercent <= 0;
        badge.textContent = `−${discountPercent} %`;
      });

      this.querySelectorAll('[data-bundle-savings]').forEach((el) => {
        el.hidden = savings <= 0;
        const amount = el.querySelector('[data-bundle-savings-amount]');
        if (amount) amount.textContent = this.formatMoney(savings);
      });

      this.querySelectorAll('[data-bundle-compare]').forEach((el) => {
        el.hidden = savings <= 0;
        el.textContent = this.formatMoney(subtotal);
      });

      // hint + CTA validation: cart button only active from the minimum quantity
      const hint = this.querySelector('[data-bundle-hint]');
      if (hint) {
        const strong = (text) => `<strong class="loop-bb__hint-strong">${text}</strong>`;
        // "[discount] %" (or bare "[discount]") is rendered bold and highlighted
        const withDiscount = (template, value) =>
          template.includes('[discount] %')
            ? template.replace('[discount] %', strong(`${value} %`))
            : template.replace('[discount]', strong(value));

        if (quantity < this.minQuantity) {
          let html = hint.dataset.textMissing.replace('[count]', this.minQuantity - quantity);
          const minTier = this.activeTier(this.minQuantity);
          if (minTier && hint.dataset.textSave) {
            // strip trailing punctuation, then append the bold savings suffix
            html = html.replace(/[.!]\s*$/, '') + ' ' + strong(hint.dataset.textSave.replace('[discount]', minTier.discount));
          }
          hint.innerHTML = html;
        } else {
          const next = this.nextTier();
          if (next) {
            hint.innerHTML = withDiscount(
              hint.dataset.textNextTier.replace('[count]', next.quantity - quantity),
              next.discount
            );
          } else {
            hint.innerHTML = withDiscount(hint.dataset.textMaxTier, discountPercent);
          }
        }
      }
      if (this.checkoutButton) {
        const missing = this.minQuantity - quantity;
        this.checkoutButton.disabled = missing > 0 || this.loading;

        const label = this.checkoutButton.querySelector('.btn-text');
        const { ctaLabel, ctaMissing, ctaMissingOne, ctaUpdate } = this.checkoutButton.dataset;
        if (label && ctaLabel) {
          if (missing === 1 && ctaMissingOne) {
            label.textContent = ctaMissingOne;
          } else if (missing > 0 && ctaMissing) {
            label.textContent = ctaMissing.replace('[count]', missing);
          } else if (this.activeTxnId && ctaUpdate) {
            // dieses Bundle liegt schon im Cart — erneutes Klicken ersetzt es
            label.textContent = ctaUpdate;
          } else {
            label.textContent = ctaLabel;
          }
        }
      }

      // per-card state (all instances of the same variant stay in sync)
      this.cards.forEach((cards, variantId) => {
        const cardQuantity = this.selection.get(variantId) || 0;
        cards.forEach((card) => {
          card.classList.toggle('is-in-bundle', cardQuantity > 0);
          const input = card.querySelector('[data-quantity-value]');
          if (input) input.textContent = cardQuantity;
          const minus = card.querySelector('[data-quantity-change="-1"]');
          if (minus) minus.disabled = cardQuantity === 0;
        });
      });

      // pack tiles: dynamic per-pack price and savings per tier
      this.renderTiles();

      // Entwurf persistieren; nach addToCart (Auswahl leer) räumt das den Storage auf
      this.saveSelection();
    }

    renderTiles() {
      let minPrice = Infinity;
      this.cards.forEach((cards) => {
        const price = parseInt(cards[0].dataset.price, 10);
        if (price > 0 && price < minPrice) minPrice = price;
      });
      if (!isFinite(minPrice)) return;

      this.querySelectorAll('[data-pack-tile]').forEach((tile) => {
        const tierQuantity = parseInt(tile.dataset.packTile, 10);
        const tier = this.activeTier(tierQuantity);
        const percent = tier ? tier.discount : 0;
        const packPrice = this.discountedUnitPrice(minPrice, percent);

        this.setText('[data-tile-price]', this.formatMoney(packPrice), tile);
        this.setText('[data-tile-compare]', this.formatMoney(minPrice), tile);
        this.setText('[data-tile-savings]', this.formatMoney((minPrice - packPrice) * tierQuantity), tile);
      });
    }

    setText(selector, value, root = this) {
      root.querySelectorAll(selector).forEach((el) => (el.textContent = value));
    }

    // ---------- Auswahl im localStorage halten (Entwurf überlebt Seitenwechsel) ----------
    // Lebensdauer ist an den Shopify-Cart gekoppelt: gespeichert wird der
    // aktuelle Cart-Token; ist der Cart beim Wiederkommen weg oder ersetzt
    // (abgelaufen, Checkout abgeschlossen), verfällt auch der Entwurf.
    // Ohne Cart-Cookie greift das 14-Tage-Limit — die Lebensdauer, die
    // Shopify selbst für das Cart-Cookie verwendet.

    get storageKey() {
      return 'loop-bb:selection';
    }

    getCartToken() {
      const match = document.cookie.match(/(?:^|;\s*)cart=([^;]+)/);
      return match ? match[1] : null;
    }

    saveSelection() {
      try {
        // solange ein Bundle dieses Entwurfs im Cart liegt (activeTxnId),
        // Eintrag auch bei leerer Auswahl behalten — sonst würde beim nächsten
        // Hinzufügen das alte Bundle nicht mehr ersetzt
        if (!this.selection.size && !this.activeTxnId) {
          localStorage.removeItem(this.storageKey);
          return;
        }
        localStorage.setItem(
          this.storageKey,
          JSON.stringify({
            items: Array.from(this.selection.entries()),
            txnId: this.activeTxnId || null,
            cartToken: this.getCartToken(),
            savedAt: Date.now(),
          })
        );
      } catch {
        // localStorage kann blockiert sein (Private Mode) — dann eben ohne Entwurf
      }
    }

    restoreSelection() {
      try {
        const raw = localStorage.getItem(this.storageKey);
        if (!raw) return;
        const data = JSON.parse(raw);
        const maxAge = 14 * 24 * 60 * 60 * 1000;
        const expired = !data.savedAt || Date.now() - data.savedAt > maxAge;
        // nur verwerfen, wenn beim Speichern ein Cart existierte und der jetzt
        // ein anderer/keiner mehr ist — ein neu entstandener Cart ist kein Ablauf
        const cartGone = data.cartToken && data.cartToken !== this.getCartToken();
        if (expired || cartGone) {
          localStorage.removeItem(this.storageKey);
          return;
        }
        this.activeTxnId = data.txnId || null;
        for (const [variantId, quantity] of data.items || []) {
          if (this.cards.has(variantId) && quantity > 0) {
            this.selection.set(variantId, quantity);
          }
        }
      } catch {
        // defekte/blockierte Daten ignorieren
      }
    }

    // Liegt das gemerkte Bundle gar nicht mehr im Cart (im Drawer entfernt),
    // wird der Entwurf wieder ein normaler "In den Warenkorb"-Fall.
    async verifyActiveBundle() {
      if (!this.activeTxnId) return;
      try {
        const cart = await fetch(`${theme.routes.cart_url}.js`).then((response) => response.json());
        const exists = cart.items.some((item) => item.properties?._bundleId === this.activeTxnId);
        if (!exists) {
          this.activeTxnId = null;
          this.render();
        }
      } catch {}
    }

    toggleSummary(open) {
      if (!this.summary) return;
      if (open && this.totalQuantity === 0) open = false;
      this.summaryOpen = open;
      this.summary.classList.toggle('is-open', open);
      this.summaryToggle?.setAttribute('aria-expanded', open);
    }

    // Tooltip-Liste der gewählten Produkte (Bild, Titel, Menge); DOM statt
    // innerHTML, damit Produkttitel nie als HTML interpretiert werden
    renderSummary() {
      const list = this.querySelector('[data-summary-list]');
      if (!list) return;
      list.textContent = '';
      this.selection.forEach((quantity, variantId) => {
        const card = this.cards.get(variantId)[0];
        const item = document.createElement('div');
        item.className = 'loop-bb__summary-item';

        if (card.dataset.image) {
          const img = document.createElement('img');
          img.src = card.dataset.image;
          img.alt = '';
          img.width = 48;
          img.height = 48;
          item.appendChild(img);
        }

        const qty = document.createElement('span');
        qty.className = 'loop-bb__summary-item-qty';
        qty.textContent = `${quantity}×`;
        item.appendChild(qty);

        const title = document.createElement('span');
        title.className = 'loop-bb__summary-item-title';
        title.textContent = card.dataset.title;
        item.appendChild(title);

        list.appendChild(item);
      });
    }

    // zentrales Produktdetail-Modal (loop-product-modal.js) öffnen;
    // der Hinzufügen-Button im Modal legt hier ins Bundle statt in den Warenkorb
    openDetail(productId) {
      const Modal = customElements.get('loop-product-modal');
      if (!Modal) return;
      Modal.getOrCreate().open(productId, {
        onAdd: (variantId, quantity) => this.changeQuantity(variantId, quantity),
      });
    }

    showError(message) {
      const box = this.querySelector('[data-bundle-error]');
      if (box) {
        box.textContent = message;
        box.hidden = !message;
      }
    }

    async addToCart() {
      if (this.loading || this.totalQuantity < this.minQuantity) return;
      this.loading = true;
      this.checkoutButton?.classList.add('is-loading');
      this.showError('');

      try {
        const productVariants = [];
        this.selection.forEach((quantity, variantId) => {
          const card = this.cards.get(variantId)[0];
          productVariants.push({
            shopifyId: Number(variantId),
            productShopifyId: Number(card.dataset.productId),
            quantity,
          });
        });

        let txnId = null;
        let sellingPlanId = this.sellingPlanId;

        if (this.api) {
          const details = await this.api.getDetails();
          txnId = await this.api.createTransaction({
            boxSizeId: this.resolveBoxSizeId(details),
            discountId: this.resolveDiscountId(details),
            sellingPlanShopifyId: sellingPlanId ? Number(sellingPlanId) : null,
            productVariants,
          });

          // Loop wendet den Rabatt nicht selbst an: pro Stufe existiert ein
          // normaler Shopify-Discount-Code (discounts[].code). Den Code der
          // erreichten Stufe an die Session hängen (setzt das discount_code-
          // Cookie), damit der Checkout ihn automatisch zieht.
          const discount = this.resolveDiscount(details);
          if (discount?.code) {
            await fetch(`/discount/${encodeURIComponent(discount.code)}`).catch(() => {});
          }
        }

        const bundleProperties = {};
        if (txnId) bundleProperties._bundleId = txnId;
        if (this.dataset.cartDisplayName) bundleProperties.__cartDisplayName = this.dataset.cartDisplayName;

        const items = productVariants.map((variant) => {
          const line = { id: variant.shopifyId, quantity: variant.quantity };
          if (Object.keys(bundleProperties).length) line.properties = bundleProperties;
          if (sellingPlanId) line.selling_plan = Number(sellingPlanId);
          return line;
        });

        // Zeilen eines früher hinzugefügten Bundles dieses Entwurfs: erneutes
        // Hinzufügen ERSETZT das Bundle im Cart, statt ein zweites anzulegen.
        // Erst neu hinzufügen, dann alte Zeilen entfernen — schlägt das Add
        // fehl, bleibt das alte Bundle unangetastet.
        let staleKeys = [];
        if (this.activeTxnId) {
          try {
            const currentCart = await fetch(`${theme.routes.cart_url}.js`).then((r) => r.json());
            staleKeys = currentCart.items
              .filter((item) => item.properties?._bundleId === this.activeTxnId)
              .map((item) => item.key);
          } catch {}
        }

        // request fresh drawer HTML with the LAST cart call, so it shows the final state
        const sectionsToBundle = [];
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:bundled-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
        );
        const sectionsPayload = {
          sections: sectionsToBundle.join(','),
          sections_url: window.location.pathname,
        };

        const addBody = staleKeys.length ? { items } : { items, ...sectionsPayload };
        const response = await fetch(`${theme.routes.cart_add_url}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(addBody),
        });
        if (!response.ok) {
          const json = await response.json().catch(() => ({}));
          throw new Error(json.description || `Add to cart failed: ${response.status}`);
        }
        let parsedState = await response.json();

        if (staleKeys.length) {
          const updates = {};
          staleKeys.forEach((key) => (updates[key] = 0));
          const updateResponse = await fetch(`${theme.routes.cart_update_url}.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ updates, ...sectionsPayload }),
          });
          if (updateResponse.ok) parsedState = await updateResponse.json();
        }

        const drawer = document.querySelector('cart-drawer');
        if (!drawer) {
          window.location.href = theme.routes.cart_url;
          return;
        }

        // let the drawer re-render itself with the fresh section HTML
        const cartJson = await fetch(theme.routes.cart_url, theme.utils.fetchConfig('json', 'GET')).then((r) =>
          r.json()
        );
        cartJson.sections = parsedState.sections;
        // cart.js re-renders the drawer and re-runs Loop's bundle grouping
        // itself. Its a11y focus handling can throw on bundle-only carts —
        // that happens after the DOM update, so it must not fail the add.
        try {
          theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'product-form', cart: cartJson });
        } catch (error) {
          console.warn('[loop-bundle-builder] cart update subscriber error', error);
        }

        drawer.show(this.checkoutButton);

        // loop_bundle.js may still force a reload when a bundle was already in
        // the cart — reopen the drawer after it (flag self-clears otherwise)
        try {
          sessionStorage.setItem('loop-bb:open-cart-drawer', '1');
          setTimeout(() => sessionStorage.removeItem('loop-bb:open-cart-drawer'), 6000);
        } catch {}

        this.loading = false;
        this.checkoutButton?.classList.remove('is-loading');
        // Auswahl bleibt als Entwurf erhalten — der Kunde kann sein Bundle
        // beim nächsten Besuch weiter bearbeiten; das neue txnId merken,
        // damit erneutes Hinzufügen dieses Bundle ersetzt
        if (txnId) this.activeTxnId = txnId;
        this.render();
      } catch (error) {
        console.error('[loop-bundle-builder]', error);
        this.showError(this.dataset.errorText || 'Das Bundle konnte nicht hinzugefügt werden. Bitte versuche es erneut.');
        this.loading = false;
        this.checkoutButton?.classList.remove('is-loading');
        this.render();
      }
    }

    // RANGE bundles usually expose one box size covering min..max; pick the one
    // enclosing the current quantity, else the admin default, else the first.
    resolveBoxSizeId(details) {
      const boxSizes = details?.boxSizes || [];
      const quantity = this.totalQuantity;
      const match = boxSizes.find(
        (box) => quantity >= (box.minSize ?? 0) && (box.maxSize == null || quantity <= box.maxSize)
      );
      return (match || boxSizes.find((box) => box.defaultSelected) || boxSizes[0])?.id ?? null;
    }

    // Quantity-conditioned Loop discount matching the reached tier, so the
    // discount at checkout comes from Loop, not from the theme.
    resolveDiscount(details) {
      const discounts = (details?.discounts || []).filter(
        (discount) =>
          discount.conditionType === 'QUANTITY' && (discount.minCartQuantity ?? 0) <= this.totalQuantity
      );
      if (!discounts.length) return null;
      discounts.sort((a, b) => (b.minCartQuantity ?? 0) - (a.minCartQuantity ?? 0));
      return discounts[0];
    }

    resolveDiscountId(details) {
      return this.resolveDiscount(details)?.id ?? null;
    }
  }

  customElements.define('loop-bundle-builder', LoopBundleBuilder);
})();
